import { baht, daysUntil } from './format';
import type { DateRange, EventFilters, MusicEvent, Performer } from '../types/marketplace';

export function maxSlotBudget(event: MusicEvent): number {
  return Math.max(...event.slots.map((s) => s.budget));
}

export function minSlotBudget(event: MusicEvent): number {
  return Math.min(...event.slots.map((s) => s.budget));
}

export function budgetLabel(event: MusicEvent): string {
  const min = minSlotBudget(event);
  const max = maxSlotBudget(event);
  return min === max ? baht(max) : `${baht(min)}–${baht(max)}`;
}

export function openSlots(event: MusicEvent): number {
  return event.slots.filter((s) => !s.filledBy).length;
}

export function slotsLabel(event: MusicEvent): string {
  const open = openSlots(event);
  if (event.slots.length === 1) return open ? '1 act wanted' : 'Line-up full';
  return `${open} of ${event.slots.length} slots open`;
}

export function travelCost(performer: Performer, km: number): number {
  return Math.round(km * 2 * performer.travelCostPerKm);
}

export function withinDateRange(iso: string, range: DateRange): boolean {
  const days = daysUntil(iso);
  if (range === 'week') return days <= 7;
  if (range === 'month') return days <= 31;
  if (range === 'quarter') return days <= 92;
  return true;
}

export const defaultFilters: EventFilters = {
  dateRange: 'any',
  maxDistance: 200,
  budget: [0, 100000],
  types: [],
  genres: [],
  multiActOnly: false,
};

export function matchesFilters(event: MusicEvent, f: EventFilters): boolean {
  if (!withinDateRange(event.date, f.dateRange)) return false;
  if (event.distanceKm > f.maxDistance) return false;
  const top = maxSlotBudget(event);
  if (top < f.budget[0] || minSlotBudget(event) > f.budget[1]) return false;
  if (f.types.length && !f.types.includes(event.type)) return false;
  if (f.genres.length && !event.genres.some((g) => f.genres.includes(g))) return false;
  if (f.multiActOnly && event.slots.length < 2) return false;
  return true;
}

export function activeFilterCount(f: EventFilters): number {
  let n = 0;
  if (f.dateRange !== 'any') n++;
  if (f.maxDistance < defaultFilters.maxDistance) n++;
  if (f.budget[0] > 0 || f.budget[1] < defaultFilters.budget[1]) n++;
  n += f.types.length + f.genres.length;
  if (f.multiActOnly) n++;
  return n;
}
